import { mapGroupBy } from '@plato/shared-util/map';
import type { CrmTaskDTO } from '../../../server/dto/crm-task-dto.ts';
import type { InsInsightOutcomeDTO } from '../../../server/dto/ins-insight-outcome-dto.ts';
import type {
    ActivityFeedQuoteOrderPayload,
    ActivityFeedVisitPayload,
} from '../../../server/types/customer-activity-feed-types.ts';

export type VisitLoggedEntry = { key: string } & (
    | { type: 'quote'; quote: ActivityFeedQuoteOrderPayload }
    | { type: 'task'; task: CrmTaskDTO }
    | { type: 'outcome'; outcome: InsInsightOutcomeDTO }
);

/**
 * What was logged on a visit, in display order: each linked quote with its tasks, the
 * other tasks, then the insight outcomes. A task sits under the first quote it links to.
 */
export function orderVisitLogged({
    tasks,
    outcomes,
    quotes,
}: Pick<ActivityFeedVisitPayload, 'tasks' | 'outcomes' | 'quotes'>): VisitLoggedEntry[] {
    const quoteIds = new Set(quotes.map((quote) => quote.sales_order_id));
    const tasksByQuote = mapGroupBy(
        tasks,
        (task) => task.links.find((link) => quoteIds.has(link.modelId))?.modelId,
    );
    const toTaskEntry = (task: CrmTaskDTO): VisitLoggedEntry => ({ key: task.taskId, type: 'task', task });

    return [
        ...quotes.flatMap((quote): VisitLoggedEntry[] => [
            { key: quote.sales_order_id, type: 'quote', quote },
            ...(tasksByQuote.get(quote.sales_order_id) ?? []).map(toTaskEntry),
        ]),
        ...(tasksByQuote.get(undefined) ?? []).map(toTaskEntry),
        ...outcomes.map((outcome): VisitLoggedEntry => ({ key: outcome.id, type: 'outcome', outcome })),
    ];
}
